import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeCfg, NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { NodeSpec, RequiredSockets } from './NodeSpec';
import { accessor } from 'src/store';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

export const createMaterialUiNodeSpec: NodeSpec<NodeCfg<'createMaterial'>> = {
    requiredSockets: (node, availableInputSockets) => {
        const requiredSockets: ReturnType<RequiredSockets<'createMaterial', NodeSocketKindMap['createMaterial']>> = {
            required: [ 'trigger' ],
            conditionallyRequired: [],
        };
        if (node.controlValues.snCfg.kind === 'direct') {
            requiredSockets.required.push('sn');
        } else {
            requiredSockets.required.push(...availableInputSockets.filter(socket => socket.startsWith('snInput_')));
        }
        return requiredSockets;
    },
    cfg: () => ({
        disabled: false,
        controlValues: {
            allowGet: false,
            location: { id: '' },
            materialModelId: '',
            snCfg: { kind: 'direct' },
            udfs: {},
        },
        id: getUID(),
        kind: 'createMaterial',
        width: 220,
    }),
    controls: nodeCfg => ({
        allowGet: defineControlOpts({
            cfg: { kind: 'boolean', label: 'Get or Create' },
            value: nodeCfg.controlValues.allowGet,
            on: { 'update:value': defaultControlUpdateValueHandler },
        }),
        location: defineControlOpts({
            value: nodeCfg.controlValues.location,
            cfg: { kind: 'location' },
            on: { 'update:value': defaultControlUpdateValueHandler },
        }),
        materialModelId: defineControlOpts({
            cfg: {
                kind: 'materialModelSelect',
                label: 'Model Number',
            },
            on: {
                'update:value': () => {}, // no-op, we only care about the select event
                'select': (materialModel, { flowEventEmitter }) => {
                    const serialNumberFormat = materialModel.materialModelSnCfg || materialModel.materialClassSnCfg;
                    const controlValues: NodeCfg<'createMaterial'>['controlValues'] = {
                        ...nodeCfg.controlValues,
                        materialModelId: materialModel.materialModelId,
                        snCfg: serialNumberFormat
                            ? {
                                fmtInputs: serialNumberFormat.tokens.filter(token => token.type === 'input').map(token => token.name),
                                kind: 'formatter',
                            }
                            : {
                                kind: 'direct',
                            },
                        udfs: materialModel.materialClassUdfSchemas.reduce((udfs, schema) => {
                            udfs[schema.id] = {
                                name: schema.name,
                                required: schema.validation_required,
                                type: schema.data_type,
                            };
                            return udfs;
                        }, {} as NodeCfg<'createMaterial'>['controlValues']['udfs']),
                    };
                    flowEventEmitter.emit('updateNodeCfg', { newNodeCfg: { ...nodeCfg, controlValues } });
                },
            },
            // placeholder: 'Select a material model',
            value: nodeCfg.controlValues.materialModelId,
        }),
    }),
    suppressNode: () => !accessor.testFeatureFlagRequirements([ 'unstableWorkOrders' ]), // this shouldn't be needed, but we need to conditionally enable the WO socket first
    // extraComponent: () => import('./inspector/CreateMaterial.helper.vue').then(m => m.default),
    inputs: nodeCfg => {
        // FUTURE: udfData has a `required` boolean that we can use to style the optional udf inputs
        const udfs = Object.fromEntries<{ socket: 'string' | 'varValue'; label: string }>(Object.entries(nodeCfg.controlValues.udfs).map(([ udfId, udf ]) => [
            `udfInput_${udfId}`,
            { socket: udf.type === 'number' ? 'varValue' : 'string', label: `${udf.name} - UDF Input` },
        ]));
        const serialNumberInputs = nodeCfg.controlValues.snCfg.kind === 'direct'
            ? { sn: { socket: 'string' as const, label: 'Serial Number' } }
            : Object.fromEntries<{ socket: 'string'; label: string }>(nodeCfg.controlValues.snCfg.fmtInputs.map(input => [
                `snInput_${input}`,
                { socket: 'string', label: `${input} - Serial Number Input` },
            ]));
        return {
            trigger: { socket: 'event', label: 'Trigger' },
            workOrderId: { socket: 'string', label: 'Work Order ID' },
            ...serialNumberInputs,
            ...udfs,
        };
    },
    kindLabel: 'Get Or Create Material',
    nodeCategory: 'material',
    outputs: () => ({
        next: { socket: 'event', label: 'Next' },
        materialId: { socket: 'string', label: 'Material ID' },
        serialNumber: { socket: 'string', label: 'Serial Number' },
    }),
    validator: node => {
        const errors: Record<string, string> = {};
        if ('deployParamKind' in node.controlValues.location) {
            if (!node.controlValues.location.deployParamId) {
                errors.location = 'Location deployment parameter is required';
            }
        } else if (!node.controlValues.location.id) {
            errors.location = 'Location is required';
        }
        if (!node.controlValues.materialModelId) {
            errors.materialModelId = 'Model Number is required';
        }
        /**
         * FUTURE: This should be a warning not an error that prevents save.
         * This loop throw an error if a udf input is not present, but the default could be set at the material model.
         * Today if neither are supplied the plv8 function will error.
         */
        // for (const udfId of Object.keys(node.controlValues.udfs)) {
        //     if (!Object.values(flow.connections).find(c => c.targetNodeId === node.id && c.targetNodePort === `udfInput_${udfId}`)) {
        //         errors[`udfInput_${udfId}`] = 'UDF input is required';
        //     }
        // }
        return Object.keys(errors).length ? errors : true;
    },
};
